import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import { QUERY_STATUS } from "constants/constants";
import { canUserPerformAction } from "access-roles/general-functions";
import { FAF_DECLARATION_ROLES } from "access-roles/roles";
import { useUserContext } from "api/user/UserProvider";
import {
  createDeclarationQuery,
  getDeclarationByIdQuery,
  getDeclarationWindowQuery,
  submitDeclarationQuery,
  updateDeclarationQuery,
} from "./queries";

const DeclarationContext = createContext({});

export const useDeclarationContext = () => useContext(DeclarationContext);

export const DeclarationProvider = ({ ...props }) => {
  const { userRoleList } = useUserContext();

  // /window returns { up_to_date, weeks: [{ week_start_date, state, declaration_id,
  // points_total }] } — 5-week carousel [current − 2 … current + 2].
  const [declarationWindow, setDeclarationWindow] = useState(null);
  const [loadWindowStatus, setLoadWindowStatus] = useState(QUERY_STATUS.INITIAL);

  // Persisted draft: created at the end of the weekly declaration steps to obtain
  // the declaration_id that child entities (prospect, need-review…) will require.
  // Resuming an already-open server draft is handled by a later run: the
  // /window carousel is deliberately lightweight (no half_day_absences and no counters).
  const [draftDeclaration, setDraftDeclaration] = useState(null);
  const [createStatus, setCreateStatus] = useState(QUERY_STATUS.INITIAL);
  const [updateStatus, setUpdateStatus] = useState(QUERY_STATUS.INITIAL);

  // Submitted declaration (/submit response): carries the server-computed points_total.
  const [submittedDeclaration, setSubmittedDeclaration] = useState(null);
  const [submitStatus, setSubmitStatus] = useState(QUERY_STATUS.INITIAL);

  const getDeclarationWindow = useCallback(async () => {
    if (
      !canUserPerformAction({
        userRoles: userRoleList,
        requiredRoles: FAF_DECLARATION_ROLES.ROLE_GET_DECLARATION,
      })
    )
      return;
    setLoadWindowStatus(QUERY_STATUS.LOADING);
    try {
      const result = await getDeclarationWindowQuery();
      setDeclarationWindow(result);
      setLoadWindowStatus(QUERY_STATUS.SUCCESS);
    } catch (e) {
      console.error("getDeclarationWindow", e);
      setLoadWindowStatus(QUERY_STATUS.ERROR);
    }
  }, [userRoleList]);

  // Resuming a draft (click on a DRAFT cell of the carousel): reloads the
  // full detail and sets it as the current draft so the rest of the wizard
  // (PATCH fields, submit, child entities attached to declaration_id) operates on it.
  const getDeclarationById = useCallback(
    async (id) => {
      if (
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: FAF_DECLARATION_ROLES.ROLE_GET_DECLARATION,
        })
      )
        return null;
      try {
        const result = await getDeclarationByIdQuery({ id });
        setDraftDeclaration(result);
        return result;
      } catch (e) {
        console.error("getDeclarationById", e);
        throw e;
      }
    },
    [userRoleList]
  );

  const createDeclaration = useCallback(
    async (payload) => {
      // Mutation guard fails loudly (vs a read → []): a user carrying the FAF tab role but
      // missing the granular mutation role must be blocked at the step, not silently walked to
      // a "0 point" recap with nothing persisted. handleNext catches this → commit error.
      if (
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: FAF_DECLARATION_ROLES.ROLE_CREATE_DECLARATION,
        })
      )
        throw new Error("faf_missing_role");
      setCreateStatus(QUERY_STATUS.LOADING);
      try {
        const result = await createDeclarationQuery({ payload });
        setDraftDeclaration(result);
        setCreateStatus(QUERY_STATUS.SUCCESS);
        return result;
      } catch (e) {
        console.error("createDeclaration", e);
        setCreateStatus(QUERY_STATUS.ERROR);
        throw e;
      }
    },
    [userRoleList]
  );

  const updateDeclaration = useCallback(
    async (id, payload) => {
      // Mutation guard: fail loudly (see createDeclaration).
      if (
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: FAF_DECLARATION_ROLES.ROLE_UPDATE_DECLARATION,
        })
      )
        throw new Error("faf_missing_role");
      setUpdateStatus(QUERY_STATUS.LOADING);
      try {
        const result = await updateDeclarationQuery({ id, payload });
        setDraftDeclaration(result);
        setUpdateStatus(QUERY_STATUS.SUCCESS);
        return result;
      } catch (e) {
        console.error("updateDeclaration", e);
        setUpdateStatus(QUERY_STATUS.ERROR);
        throw e;
      }
    },
    [userRoleList]
  );

  const submitDeclaration = useCallback(
    async (id) => {
      // Mutation guard: fail loudly (see createDeclaration).
      if (
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: FAF_DECLARATION_ROLES.ROLE_UPDATE_DECLARATION,
        })
      )
        throw new Error("faf_missing_role");
      setSubmitStatus(QUERY_STATUS.LOADING);
      try {
        const result = await submitDeclarationQuery({ id });
        setSubmittedDeclaration(result);
        setSubmitStatus(QUERY_STATUS.SUCCESS);
        return result;
      } catch (e) {
        console.error("submitDeclaration", e);
        setSubmitStatus(QUERY_STATUS.ERROR);
        throw e;
      }
    },
    [userRoleList]
  );

  const values = useMemo(
    () => ({
      declarationWindow,
      loadWindowStatus,
      getDeclarationWindow,
      getDeclarationById,
      draftDeclaration,
      setDraftDeclaration,
      createDeclaration,
      createStatus,
      updateDeclaration,
      updateStatus,
      submitDeclaration,
      submittedDeclaration,
      setSubmittedDeclaration,
      submitStatus,
    }),
    [
      declarationWindow,
      loadWindowStatus,
      getDeclarationWindow,
      getDeclarationById,
      draftDeclaration,
      createDeclaration,
      createStatus,
      updateDeclaration,
      updateStatus,
      submitDeclaration,
      submittedDeclaration,
      submitStatus,
    ]
  );

  return <DeclarationContext.Provider value={values} {...props} />;
};
